import axios, {
  AxiosHeaders,
  type AxiosRequestHeaders,
  type AxiosResponse,
} from 'axios';
import { ALL_ROUTES, API_BASE_URL } from './constants';

/**
 * Single Axios wrapper for every backend call — services must go through this
 * rather than importing axios directly.
 *
 * Responsibilities:
 *  - prefixes relative paths with the gateway base URL from `.env.<mode>`
 *  - attaches the B2C bearer token and the APIM subscription key
 *  - clears the session and bounces to login on 401/440
 *
 * Like the MicroUI APIService, an Axios error that carries a `response` is
 * returned as-is instead of thrown, so callers must inspect status/shape rather
 * than assume a rejection means a network failure.
 */

const APIM_SUBSCRIPTION_HEADER = 'Ocp-Apim-Subscription-Key';
const apimSubscriptionKey = (
  import.meta.env.VITE_NOVA_APIM_SUBSCRIPTION_KEY ?? ''
).trim();

/**
 * `request-user-id` is what the connector reads to attribute a request.
 * DataNovaProxy overwrites it server-side from the validated token, so it is
 * inert in every environment now that nothing calls a connector directly - kept
 * only for a locally-run connector (see .env.localhost).
 */
const REQUEST_USER_ID = '3';

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;

/**
 * Registers the source of the bearer token (`acquireApiToken`, from
 * `main.tsx`).
 *
 * Injected rather than imported so this module stays free of MSAL: it is pulled
 * into every service test, and importing the auth stack there made a cold test
 * import slow enough to hit Vitest's default timeout.
 */
export const setTokenProvider = (provider: TokenProvider): void => {
  tokenProvider = provider;
};

export const clearSession = (): void => {
  localStorage.clear();
  sessionStorage.clear();
};

/**
 * A 401 (unauthorized) or 440 (session expired) means the token is no longer
 * usable — drop everything and send the user back to login.
 */
export const handleUnauthorizedRequest = (status?: number): void => {
  if (status === 401 || status === 440) {
    clearSession();
    window.location.href = ALL_ROUTES.LOGIN;
  }
};

const buildUrl = (path: string): string =>
  path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

const appendQueryParams = (url: string, query?: Record<string, unknown>) => {
  if (!query) return url;

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((entry) => params.append(key, String(entry)));
    } else {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
};

class APIService {
  private async buildHeaders(
    headers?: AxiosRequestHeaders,
  ): Promise<AxiosHeaders> {
    const requestHeaders = new AxiosHeaders({
      'content-type': 'application/json',
      'request-user-id': REQUEST_USER_ID,
      ...headers,
    });

    if (apimSubscriptionKey && !requestHeaders.has(APIM_SUBSCRIPTION_HEADER)) {
      requestHeaders.set(APIM_SUBSCRIPTION_HEADER, apimSubscriptionKey);
    }

    // Resolved per request, not once at sign-in: `acquireApiToken` refreshes the
    // token silently when it has expired, and these pages are left open for
    // hours at a time. It returns the complete header value, scheme included,
    // because a statically pasted token may already carry its own scheme.
    const token = await tokenProvider();
    if (token && !requestHeaders.has('Authorization')) {
      requestHeaders.set('Authorization', token);
    }

    return requestHeaders;
  }

  async get<T>(
    path: string,
    params?: Record<string, unknown>,
    headers?: AxiosRequestHeaders,
  ): Promise<AxiosResponse<T> | null> {
    const url = appendQueryParams(buildUrl(path), params);
    try {
      return await axios.get<T>(url, {
        headers: await this.buildHeaders(headers),
      });
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  async post<T>(
    path: string,
    data?: unknown,
    headers?: AxiosRequestHeaders,
  ): Promise<AxiosResponse<T> | null> {
    try {
      return await axios.post<T>(buildUrl(path), data, {
        headers: await this.buildHeaders(headers),
      });
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  /**
   * Returns the error's response when the server answered, `null` when the
   * request never completed (network/CORS/timeout). Either way the caller falls
   * back to an empty result instead of crashing the page — matching how the
   * legacy `CommonFunctions` helpers resolved on failure.
   */
  private handleError<T>(error: unknown): AxiosResponse<T> | null {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        handleUnauthorizedRequest(error.response.status);
        return error.response as AxiosResponse<T>;
      }
      console.error('API request failed without a response:', error.message);
      return null;
    }

    console.error('Unexpected API error:', error);
    return null;
  }
}

const apiService = new APIService();
export default apiService;
